import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { SeoProvider } from "@/components/shared/SEO";

// Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ProductsPage from "@/pages/ProductsPage";
import IndustriesPage from "@/pages/IndustriesPage";
import SustainabilityPage from "@/pages/SustainabilityPage";
import ClientsPage from "@/pages/ClientsPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import { AnalyticsTracker } from "@/hooks/useAnalytics";

// Admin Pages
import { lazy, Suspense } from 'react';
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("@/pages/admin/AdminCategories"));
const AdminInquiries = lazy(() => import("@/pages/admin/AdminInquiries"));
const AdminContacts = lazy(() => import("@/pages/admin/AdminContacts"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminQuotations = lazy(() => import("@/pages/admin/AdminQuotations"));
const AdminClients = lazy(() => import("@/pages/admin/AdminClients"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminHeroSlides = lazy(() => import("@/pages/admin/AdminHeroSlides"));
const AdminSustainability = lazy(() => import("@/pages/admin/AdminSustainability"));
const AdminAboutUs = lazy(() => import("@/pages/admin/AdminAboutUs"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AdminAuditLogs"));

// Context
import { AuthProvider } from "@/context/AuthContext";

function App() {
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG' && !window.location.pathname.startsWith('/admin')) {
        e.preventDefault();
      }
    };

    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG' && !window.location.pathname.startsWith('/admin')) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  return (
    <HelmetProvider>
    <SeoProvider>
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AnalyticsTracker />
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-4 border-[#14532D] border-t-transparent rounded-full animate-spin"></div></div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:categoryId" element={<ProductsPage />} />
              <Route path="/industries" element={<IndustriesPage />} />
              <Route path="/sustainability" element={<SustainabilityPage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/inquiries" element={<AdminInquiries />} />
              <Route path="/admin/contacts" element={<AdminContacts />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
              <Route path="/admin/quotations" element={<AdminQuotations />} />
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/hero-slides" element={<AdminHeroSlides />} />
              <Route path="/admin/sustainability" element={<AdminSustainability />} />
              <Route path="/admin/about" element={<AdminAboutUs />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogs />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
    </SeoProvider>
    </HelmetProvider>
  );
}

export default App;
